import React, { useState } from 'react';
import { Book, Search, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface Term {
  term: string;
  definition: string;
  category: string;
  example?: string;
}

const financialTerms: Term[] = [
  {
    term: "50/30/20 Rule",
    definition: "A budgeting method that allocates 50% of income to needs, 30% to wants, and 20% to savings.",
    category: "Budgeting",
    example: "In a $5,000 monthly budget: $2,500 for needs, $1,500 for wants, $1,000 for savings"
  },
  {
    term: "Emergency Fund",
    definition: "Money set aside for unexpected expenses or financial emergencies.",
    category: "Savings",
    example: "3-6 months of living expenses saved in an easily accessible account"
  },
  {
    term: "Net Worth",
    definition: "The total value of assets minus liabilities.",
    category: "Personal Finance",
    example: "$250,000 in assets - $150,000 in liabilities = $100,000 net worth"
  },
  {
    term: "Compound Interest",
    definition: "Interest earned on both the initial principal and accumulated interest.",
    category: "Investing",
    example: "$1,000 at 5% annually becomes $1,276 after 5 years due to compounding"
  },
  {
    term: "Tax Deduction",
    definition: "An expense that reduces your taxable income.",
    category: "Taxes",
    example: "Mortgage interest, charitable donations, student loan interest"
  },
  {
    term: "Debt-to-Income Ratio",
    definition: "Monthly debt payments divided by gross monthly income.",
    category: "Credit",
    example: "$1,500 monthly debt / $5,000 monthly income = 30% DTI"
  }
];

export const FinancialTerms: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [expandedTerm, setExpandedTerm] = useState<string | null>(null);

  const categories = Array.from(new Set(financialTerms.map(term => term.category)));

  const filteredTerms = financialTerms.filter(term => {
    const matchesSearch = term.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         term.definition.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || term.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-xl bg-emerald-500/10">
          <Book className="w-6 h-6 text-emerald-500" />
        </div>
        <h2 className="text-2xl font-semibold">Financial Terms</h2>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search financial terms..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
          />
        </div>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="glass-input px-4 py-2 rounded-lg md:w-48"
        >
          <option value="">All Categories</option>
          {categories.map(category => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>
      </div>

      <div className="space-y-4">
        {filteredTerms.map(term => (
          <motion.div
            key={term.term}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-6 border border-emerald-500/20"
          >
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setExpandedTerm(expandedTerm === term.term ? null : term.term)}
            >
              <div>
                <h3 className="font-semibold text-lg">{term.term}</h3>
                <span className="text-sm text-emerald-500">{term.category}</span>
              </div>
              <ChevronDown
                className={`w-5 h-5 text-slate-400 transition-transform ${
                  expandedTerm === term.term ? 'rotate-180' : ''
                }`}
              />
            </div>

            <AnimatePresence>
              {expandedTerm === term.term && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4 space-y-3"
                >
                  <p className="text-slate-300">{term.definition}</p>
                  {term.example && (
                    <div className="glass-card p-4 border border-emerald-500/20 bg-emerald-500/5">
                      <p className="text-sm">
                        <span className="text-emerald-500 font-medium">Example: </span>
                        {term.example}
                      </p>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}

        {filteredTerms.length === 0 && (
          <div className="text-center py-8 text-slate-400">
            <Book className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No terms found matching your search criteria.</p>
          </div>
        )}
      </div>
    </div>
  );
};